import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Bell, Trophy, Target, Activity as ActivityIcon, X, CheckCheck, Loader2,
} from "lucide-react";
import api from "@/lib/api";

const ICON_MAP = {
  Trophy: Trophy,
  Target: Target,
  Activity: ActivityIcon,
  X: X,
  Bell: Bell,
};

const SEVERITY_BG = {
  success: "bg-emerald-500/10 text-emerald-700",
  warning: "bg-amber-500/10 text-amber-700",
  info: "bg-blue-500/10 text-blue-700",
};

/**
 * Phase 5C — Notification bell + dropdown panel for the Principal Portal sidebar.
 * Polls /portal/notifications/_unread-count every 30s.
 */
export default function PortalNotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get("/portal/notifications/_unread-count");
      setUnread(data.count || 0);
    } catch {}
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portal/notifications", { params: { limit: 20 } });
      setItems(data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const togglePanel = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) await fetchList();
  };

  const onItemClick = async (n) => {
    if (!n.seen) {
      try {
        await api.post("/portal/notifications/seen", { notification_id: n.id });
      } catch {}
    }
    setOpen(false);
    fetchUnread();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/portal/notifications/mark-all-seen");
      await Promise.all([fetchUnread(), fetchList()]);
    } catch {}
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="portal-notif-bell-wrapper">
      <Button
        variant="ghost"
        size="sm"
        className="relative w-full justify-start text-xs"
        onClick={togglePanel}
        data-testid="portal-notif-bell-btn"
      >
        <Bell className="h-3 w-3 mr-2" />
        {t("portal.notif.title")}
        {unread > 0 && (
          <span
            className="absolute right-2 top-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold animate-pulse"
            data-testid="portal-notif-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute left-full ml-2 top-0 w-80 max-h-[70vh] overflow-y-auto bg-card border rounded-lg shadow-lg z-50"
          data-testid="portal-notif-panel"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b sticky top-0 bg-card">
            <span className="text-xs font-semibold">{t("portal.notif.title")}</span>
            {unread > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-[10px] px-2"
                onClick={markAll}
                data-testid="portal-notif-mark-all"
              >
                <CheckCheck className="h-3 w-3 mr-1" /> {t("portal.notif.mark_all")}
              </Button>
            )}
          </div>

          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-4 w-4 mx-auto animate-spin text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground px-4">{t("portal.notif.empty")}</div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => {
                const Icon = ICON_MAP[n.icon] || Bell;
                const sevBg = SEVERITY_BG[n.severity] || SEVERITY_BG.info;
                return (
                  <li
                    key={n.id}
                    onClick={() => onItemClick(n)}
                    className={`px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors ${!n.seen ? "bg-emerald-500/5" : ""}`}
                    data-testid={`portal-notif-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`p-1.5 rounded-md ${sevBg} shrink-0`}>
                        <Icon className="h-3 w-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs ${!n.seen ? "font-semibold" : ""} truncate`}>{n.title}</div>
                        {n.body && (
                          <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>
                        )}
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      {!n.seen && (
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
